/* Nexora product template – supporting sections (video carousel). No dependencies. */
(() => {
  if (customElements.get('nexora-video-carousel')) return;

  class NexoraVideoCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-nx-vtrack]');
      this.prev = this.querySelector('[data-nx-vprev]');
      this.next = this.querySelector('[data-nx-vnext]');
      if (!this.track) return;

      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
      window.addEventListener('resize', () => this.updateArrows());
      this.updateArrows();

      // Manual-play videos: toggle on click.
      this.querySelectorAll('[data-nx-vmedia]').forEach((media) => {
        const video = media.querySelector('video');
        const button = media.querySelector('[data-nx-vplay]');
        if (!video) return;
        const toggle = () => {
          if (video.paused) {
            video.play();
          } else {
            video.pause();
          }
        };
        button?.addEventListener('click', toggle);
        if (button) video.addEventListener('click', toggle);
        video.addEventListener('play', () => media.classList.add('is-playing'));
        video.addEventListener('pause', () => media.classList.remove('is-playing'));
      });

      // Autoplay videos only while visible to save bandwidth and CPU.
      const autoplay = [...this.querySelectorAll('[data-nx-autoplay] video')];
      autoplay.forEach((v) => { v.muted = true; v.playsInline = true; });
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (autoplay.length && 'IntersectionObserver' in window && !reduceMotion) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              if (video.preload === 'none') video.preload = 'metadata';
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        }, { threshold: 0.35 });
        autoplay.forEach((v) => this.observer.observe(v));
      }
    }

    disconnectedCallback() { this.observer?.disconnect(); }

    slideWidth() {
      const slide = this.track.querySelector('.nx-vslider__slide');
      return slide ? slide.getBoundingClientRect().width : this.track.clientWidth;
    }

    step(dir) {
      this.track.scrollBy({ left: dir * this.slideWidth(), behavior: 'smooth' });
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    }
  }

  customElements.define('nexora-video-carousel', NexoraVideoCarousel);
})();

/* Review carousel: horizontal scroll with arrow buttons (swipe on touch). */
(() => {
  if (customElements.get('nexora-review-carousel')) return;
  class NexoraReviewCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-nx-rtrack]');
      this.prev = this.querySelector('[data-nx-rprev]');
      this.next = this.querySelector('[data-nx-rnext]');
      if (!this.track) return;
      const step = (dir) => {
        const card = this.track.querySelector('.nx-review');
        const w = card ? card.getBoundingClientRect().width + 16 : this.track.clientWidth;
        this.track.scrollBy({ left: dir * w, behavior: 'smooth' });
      };
      this.prev?.addEventListener('click', () => step(-1));
      this.next?.addEventListener('click', () => step(1));
      const update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      };
      this.track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    }
  }
  customElements.define('nexora-review-carousel', NexoraReviewCarousel);
})();
